<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { api } from '../api'
import { useI18n } from '../i18n'

const route = useRoute()
const { t } = useI18n()
const activeStation = ref(null)
const template = ref(null)
const parameterForm = ref(null)
const parameterValues = ref({})
const loading = ref(true)
const reportRunning = ref(false)
const chartImageUrl = ref('')
const error = ref('')
const noData = ref(false)
const visibleParameters = computed(() => parameterForm.value?.parameters.filter((parameter) => !parameter.autoFilled) ?? [])

async function loadParameterForm() {
  const resource = template.value.kind === 'CHART' ? 'charts' : 'reports'
  const result = await api(`/${resource}/${template.value.id}/parameters`)
  parameterForm.value = result.form
  parameterValues.value = Object.fromEntries(result.form.parameters.map((parameter) => [
    parameter.name,
    parameter.selection === 'Multiple' ? [...parameter.defaultValues] : (parameter.defaultValues[0] ?? ''),
  ]))
}

async function load() {
  loading.value = true
  error.value = ''
  noData.value = false
  parameterForm.value = null
  clearChartImage()
  try {
    const [stations, detail] = await Promise.all([api('/stations'), api(`/catalog/${route.params.templateId}`)])
    activeStation.value = stations.activeStation
    template.value = detail.item
    if (activeStation.value) await loadParameterForm()
  } catch (cause) {
    error.value = cause.message
  } finally {
    loading.value = false
  }
}

function clearChartImage() {
  if (chartImageUrl.value) URL.revokeObjectURL(chartImageUrl.value)
  chartImageUrl.value = ''
}

function valuesForExport() {
  const values = {}
  for (const parameter of parameterForm.value?.parameters ?? []) {
    if (parameter.autoFilled) continue
    const value = parameterValues.value[parameter.name]
    if (parameter.selection === 'Multiple') {
      if (Array.isArray(value) && value.length) values[parameter.name] = value
    } else if (value !== '') {
      values[parameter.name] = value
    }
  }
  return values
}

function selectedOptions(parameter) {
  const selectedValues = Array.isArray(parameterValues.value[parameter.name]) ? parameterValues.value[parameter.name] : []
  return parameter.options.filter((option) => selectedValues.includes(option.value))
}

function multipleSummary(parameter) {
  const selected = selectedOptions(parameter)
  if (!selected.length) return t('chooseMultiple')
  return selected.length === 1 ? selected[0].label : t('selectedCount', { count: selected.length })
}

function allSelected(parameter) {
  return parameter.options.length > 0 && selectedOptions(parameter).length === parameter.options.length
}

function toggleAll(parameter) {
  parameterValues.value[parameter.name] = allSelected(parameter) ? [] : parameter.options.map((option) => option.value)
}

function temporalInputType(parameter) {
  if (parameter.valueType === 'java.sql.Timestamp') return 'datetime-local'
  if (parameter.valueType === 'java.sql.Date') return 'date'
  if (parameter.valueType === 'java.sql.Time') return 'time'
  return ''
}

function temporalInputValue(parameter) {
  const value = parameterValues.value[parameter.name] ?? ''
  return parameter.valueType === 'java.sql.Timestamp' ? value.replace(' ', 'T') : value
}

function updateTemporalParameter(parameter, rawValue) {
  if (parameter.valueType !== 'java.sql.Timestamp') {
    parameterValues.value[parameter.name] = rawValue
    return
  }
  const value = rawValue.replace('T', ' ')
  parameterValues.value[parameter.name] = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(value) ? `${value}:00` : value
}

function pdfFileName() {
  const title = (template.value?.displayName ?? 'report')
    .replace(/[\\/:*?"<>|\u0000-\u001F]/g, '_')
    .trim()
    .slice(0, 120) || 'report'
  return `${title}.pdf`
}

function pdfAction() {
  const resource = template.value.kind === 'CHART' ? 'charts' : 'reports'
  return `/api/${resource}/${template.value.id}/pdf/${encodeURIComponent(pdfFileName())}?ui=1`
}

async function openPdf() {
  if (!template.value) return
  error.value = ''
  noData.value = false
  const targetName = `next_reports_pdf_${Date.now()}`
  const reportWindow = window.open('about:blank', targetName)
  if (!reportWindow) {
    error.value = t('popupBlocked')
    return
  }
  reportRunning.value = true
  const removeNoDataListener = () => window.removeEventListener('message', onExportMessage)
  const onExportMessage = (event) => {
    if (event.origin !== window.location.origin || event.data?.type !== 'next-reports-export-no-data') return
    noData.value = true
    removeNoDataListener()
  }
  window.addEventListener('message', onExportMessage)
  try {
    const form = document.createElement('form')
    const values = document.createElement('input')
    form.method = 'POST'
    form.action = pdfAction()
    form.target = targetName
    form.acceptCharset = 'UTF-8'
    form.style.display = 'none'
    values.name = 'values'
    values.value = JSON.stringify(valuesForExport())
    form.append(values)
    document.body.append(form)
    form.submit()
    form.remove()
    window.setTimeout(removeNoDataListener, 60 * 1000)
  } catch (cause) {
    removeNoDataListener()
    reportWindow.close()
    error.value = cause.message ?? (template.value.kind === 'CHART' ? t('chartPdfFailed') : t('pdfFailed'))
  } finally {
    reportRunning.value = false
  }
}

async function renderChart() {
  if (!template.value || template.value.kind !== 'CHART') return
  error.value = ''
  noData.value = false
  reportRunning.value = true
  try {
    const response = await fetch(`/api/charts/${template.value.id}/image`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ values: valuesForExport() }),
    })
    if (!response.ok) {
      const payload = await response.json().catch(() => null)
      if (payload?.error?.code === 'no_data') {
        noData.value = true
        return
      }
      throw new Error(payload?.error?.message ?? t('chartFailed'))
    }
    const imageUrl = URL.createObjectURL(await response.blob())
    clearChartImage()
    chartImageUrl.value = imageUrl
  } catch (cause) {
    error.value = cause.message
  } finally {
    reportRunning.value = false
  }
}

watch(() => route.params.templateId, load, { immediate: true })
watch(parameterValues, () => { noData.value = false }, { deep: true })
onBeforeUnmount(clearChartImage)
</script>

<template>
  <section class="report-page">
    <RouterLink class="back-link" :to="{ name: 'home' }">{{ t('backToCatalog') }}</RouterLink>
    <p v-if="error" class="notice error" role="alert">{{ error }}</p>
    <div v-if="loading" class="empty">{{ t('templateLoading') }}</div>
    <template v-else-if="template">
      <div class="page-title report-title">
        <div>
          <p class="eyebrow">{{ template.kind === 'CHART' ? t('chart') : t('report') }}</p>
          <h1>{{ template.displayName }}</h1>
        </div>
        <span v-if="activeStation" class="status success">{{ t('stationBadge', { name: activeStation.displayName }) }}</span>
      </div>

      <div v-if="!activeStation" class="card empty">{{ t('chooseStationForTemplate') }}</div>
      <section v-else class="card report-parameters">
        <p class="eyebrow">{{ template.kind === 'CHART' ? t('chartParameters') : t('reportParameters') }}</p>
        <h2>{{ t('filters') }}</h2>
        <div v-if="parameterForm" class="parameter-form">
          <div v-for="parameter in visibleParameters" :key="parameter.name" class="parameter-field" :class="{ 'is-multiple': parameter.selection === 'Multiple' }">
            <span>{{ parameter.label }}<em v-if="parameter.mandatory"> *</em></span>
            <details v-if="parameter.options.length && parameter.selection === 'Multiple'" class="multi-select">
              <summary>
                <span class="multi-select-summary">{{ multipleSummary(parameter) }}</span>
                <span class="multi-select-count">{{ selectedOptions(parameter).length }}/{{ parameter.options.length }}</span>
              </summary>
              <div class="multi-select-panel">
                <button type="button" class="multi-select-toggle" @click="toggleAll(parameter)">{{ allSelected(parameter) ? t('clear') : t('selectAll') }}</button>
                <div class="multi-select-options">
                  <label v-for="option in parameter.options" :key="option.value" class="multi-select-option">
                    <input v-model="parameterValues[parameter.name]" type="checkbox" :value="option.value">
                    <span>{{ option.label }}</span>
                  </label>
                </div>
              </div>
            </details>
            <select v-else-if="parameter.options.length" v-model="parameterValues[parameter.name]" :aria-label="parameter.label">
              <option disabled value="">{{ t('selectValue') }}</option>
              <option v-for="option in parameter.options" :key="option.value" :value="option.value">{{ option.label }}</option>
            </select>
            <input v-else-if="temporalInputType(parameter)" :value="temporalInputValue(parameter)" :type="temporalInputType(parameter)" :step="parameter.valueType === 'java.sql.Timestamp' || parameter.valueType === 'java.sql.Time' ? 1 : undefined" :aria-label="parameter.label" @input="updateTemporalParameter(parameter, $event.target.value)" />
            <input v-else v-model.trim="parameterValues[parameter.name]" type="text" :aria-label="parameter.label" :placeholder="parameter.valueType" />
          </div>
        </div>
        <section v-if="noData" class="notice no-data-notice" role="alert" aria-live="polite">
          <strong>{{ t('noDataTitle') }}</strong>
          <span>{{ t('noDataHint') }}</span>
        </section>
        <button v-if="template.kind === 'REPORT'" class="button primary report-run-button" type="button" :disabled="reportRunning || !parameterForm" @click="openPdf">
          {{ reportRunning ? t('generatingPdf') : t('openPdf') }}
        </button>
        <div v-else class="chart-actions">
          <button class="button button-secondary" type="button" :disabled="reportRunning || !parameterForm" @click="renderChart">
            {{ reportRunning ? t('buildingChart') : t('buildChart') }}
          </button>
          <button class="button primary" type="button" :disabled="reportRunning || !parameterForm" @click="openPdf">
            {{ reportRunning ? t('generatingChartPdf') : t('openChartPdf') }}
          </button>
        </div>
        <div v-if="template.kind === 'CHART' && chartImageUrl" class="chart-preview">
          <img :src="chartImageUrl" :alt="t('chartAlt', { name: template.displayName })">
        </div>
      </section>
    </template>
  </section>
</template>
